import formsApi from '../../api/formsApi';
import dataUtils from '@/common/dataUtils';
import { SYNC_STATES } from '@/store/sync/sync';
import { performInMemorySearch } from '@/components/search/searchStore';
import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import i18n from '@/plugins/i18n';
import index from './index';

const DEBUG = false;
const USER_STATE_TYPE = 'formUserState';
const ROOT = { root: true };

const state = {
  forms: [],
  groups: [],
  formIdsByGroup: [],
  formsById: {},
  formReports: {},
  medias: [],
  autofill: {},
  dataLoaded: false,
  readAllDialogOpen: false,
  selectedGroups: [],
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    let unreadCount = 0;
    Object.keys(state.formsById).forEach((id) => {
      const form = state.formsById[id];
      if (form.instance == instance && !form._userState.dateread) {
        unreadCount++;
      }
    });
    console.debug('forms getUnreadCountByModuleInstance', unreadCount);
    return unreadCount;
  },
};
const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    //no-ops implementation
  },
  setFormsData(state, serverData) {
    Object.keys(serverData).forEach((key) => (state[key] = serverData[key]));
    state.dataLoaded = true;
  },
  setSelectedGroups: (state, { value }) => {
    state.selectedGroups = value;
  },
};
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all forms containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.forms,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // TODO: DELETE as soon as nowhere used
  async updateModuleApplicationCache({ state }) {
    // const { formReports, medias, formIdsByGroup, formsById, autofill } = state;
    // await updateModuleCache(moduleIndex.api, { formReports, medias, formIdsByGroup, formsById, autofill });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    //Extract medias from formReports and save to cache
    let formReportMedias = [];
    Object.keys(state.formReports).forEach((formId) => {
      formReportMedias = [...formReportMedias, ...state.formReports[formId].medias];
    });
    if (formReportMedias.length > 0) {
      formReportMedias.forEach((f) => (f.requestSuffix = '/report'));
    }
    // Important to cache all module files together (reason cache management)
    await cacheAllModuleFiles({ files: state.medias.concat(formReportMedias), moduleIndex, commit, dispatch });
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * See documents/store as an example
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, rootGetters }) {
    try {
      console.time('formsApi.getForms()');

      let response = await formsApi.getForms();
      let { formReports, medias, formIdsByGroup, formsById, autofill, groups } = response.data;
      console.timeEnd('formsApi.getForms()');
      let forms = Object.keys(formsById).map((key) => formsById[key]);
      forms.forEach((form) => {
        let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({
          moduleType: index.moduleType,
          instance: form.instance,
        });

        let loadedUserState = form._userState;
        let userStatePrototype = { proto: true, formIdFk: form.id, dateread: null }; // TODO: Refactor to get user states in a separate
        form._userState = dataUtils.getLatestUserState({ item: form, loadedUserState, userStatePrototype });
        form._displayLocation = `Formular ${form.title}`; // buildFolderPath(foldersById, '', foldersById[document.folderIdFk]);
        form._moduleTitle = moduleConfig ? moduleConfig.title : 'module not defined';
        form._path = `/forms/${form.instance}/${form.id}`;
        form._moduleId = MODULE_ID;
        form.type = 'forms';
      });
      dataUtils.applyLocalStorageUserState(formsById, USER_STATE_TYPE, 'formIdFk');

      commit('setFormsData', { forms, groups, formReports, medias, formIdsByGroup, formsById, autofill });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId: MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  async markAsRead2({ state, dispatch }, { item }) {
    let sendboxTitle = i18n.t('action.forms.read.msg', { object: item.title });
    let snackbarText = i18n.t('action.form.seen');
    console.debug('send form');
    // dispatch('markAsRead2', { item: state.formsById[formId] });
    dispatch(`sendbox/markAsRead2`, { item, sendboxTitle, snackbarText, immediate: true }, ROOT);
  },

  async sendForm({ rootState, state, dispatch }, { instance, module, data, medias, formId }) {
    // add to sendbox
    let form = state.formsById[formId];
    let action = 'SubmittedAction';
    let subject = { instance, module, data, medias, formId, userdatemodified: new Date(), _type: 'form' };
    let moduleId = 'forms';
    let payload = {};
    let userState = {};
    let title = form.title;
    let text = `${i18n.t('sendbox.type.formUserState')} ${i18n.t('action.submitted.msg', { object: form.title })}`;
    let showAsUserInfo = true;
    let showInSendbox = true;
    let writeAuditLog = true;

    form._userState.dateread = new Date(Date.now());

    if (!rootState.isConnected && state.formsById[formId].allowMultipleSubmit === 0) {
      // Show information message by mocking static form if filled form should be shown and is offline
      // Execute before exeucteAction to ensure that the mock report lands in the cache as well
      state.formsById[formId].formbuilderdata = JSON.stringify({
        fields: [
          {
            field_type: 'section_break',
            field_options: {},
            isStaticForm: true,
            label: 'Dieses Formular wird angezeigt, sobald Sie online sind',
            cid: 'c1',
          },
        ],
      });
    }
    console.debug('sendForm');
    await dispatch(
      'sendbox/performSendboxItem2Action',
      { moduleId, subject, payload, userState, title, text, action, showAsUserInfo, showInSendbox, writeAuditLog },
      { root: true }
    );

    // if offline store into module sendbox
    // Save to cacheStorage
    // let cache=await caches.open('oevpad-api');
    // let response=await cache.match('api/modules/forms/posts') ;
    // let queue=response?await response.json():[]
    // console.debug("queue",JSON.stringify(queue,null,2))
    // let formSendbox={ formValues:data, medias, formId, userdatemodified: new Date()};
    // queue.push(formSendbox)
    // cache.put('api/modules/forms/posts',Response.json(queue))
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
